import type { UniqueIdentifier } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, useTheme } from "@mui/material";
import type { ReactNode } from "react";
import { SortableDragHandle } from "./SortableDragHandle";

type SortableRowProps<Data extends { id: UniqueIdentifier }> = {
    item: Data;
    index: number;
    disabled: boolean;
    handleLabel?: string;
    renderItem: (item: Data, draggableItem: ReactNode, index: number) => ReactNode;
};

export const SortableRow = <Data extends { id: UniqueIdentifier }>(props: SortableRowProps<Data>) => {
    const { item, index, renderItem, disabled, handleLabel } = props;
    const theme = useTheme();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item.id,
        disabled,
    });

    return (
        <Box
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
            }}
            sx={{
                backgroundColor: isDragging ? theme.custom.grey[100] : "transparent",
                borderRadius: isDragging ? theme.spacing(0.5) : 0,
                position: "relative",
                zIndex: isDragging ? theme.zIndex.appBar : "auto",
            }}
        >
            {renderItem(
                item,
                <SortableDragHandle
                    attributes={attributes}
                    listeners={listeners}
                    disabled={disabled}
                    isDragging={isDragging}
                    label={handleLabel}
                />,
                index,
            )}
        </Box>
    );
};
